import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Info, ArrowRight, Wrench, Toolbox, Broom, Trash, Truck, Tag } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Configurable ranges (David can tune later).
const CONFIG = {
  "appliance-repair": {
    label: "Appliance Repair", icon: Wrench,
    base: { min: 120, max: 450 },
    modifiers: [
      { key: "appliance", label: "Appliance", type: "select", options: [
        { v: "fridge", label: "Refrigerator / Freezer", min: 40, max: 200 },
        { v: "washer", label: "Washer", min: 0, max: 100 },
        { v: "dryer", label: "Dryer", min: 0, max: 100 },
        { v: "dishwasher", label: "Dishwasher", min: 0, max: 120 },
        { v: "range", label: "Range / Oven / Cooktop", min: 20, max: 180 },
        { v: "microwave", label: "Over-the-Range Microwave", min: 0, max: 90 },
        { v: "disposal", label: "Garbage Disposal", min: -30, max: -50 },
      ]},
      { key: "gas", label: "Gas appliance? (extra safety work)", type: "toggle", min: 40, max: 90 },
    ],
  },
  "appliance-install": {
    label: "Appliance Installation", icon: Toolbox,
    base: { min: 150, max: 400 },
    modifiers: [
      { key: "unit", label: "Unit", type: "select", options: [
        { v: "range", label: "Range (electric or gas)", min: 0, max: 100 },
        { v: "dishwasher", label: "Dishwasher", min: 20, max: 120 },
        { v: "washer-dryer", label: "Washer / Dryer", min: 0, max: 80 },
        { v: "microwave", label: "Over-the-range Microwave", min: 0, max: 60 },
        { v: "fridge", label: "Refrigerator", min: -20, max: 20 },
        { v: "builtin", label: "Built-in appliance", min: 80, max: 200 },
      ]},
      { key: "haul", label: "Haul away old unit?", type: "toggle", min: 50, max: 100 },
    ],
  },
  "post-construction": {
    label: "Post-Construction Cleanup", icon: Broom,
    base: { min: 0, max: 0 }, // computed from sqft
    modifiers: [
      { key: "sqft", label: "Square footage", type: "number", min: 0, max: 0, placeholder: "e.g. 1800" },
      { key: "rate", label: "Detail level", type: "select", options: [
        { v: "rough",  label: "Rough clean (debris + broom)", ratePerSqft: [0.35, 0.55] },
        { v: "final",  label: "Final detail clean (turnover ready)", ratePerSqft: [0.55, 0.85] },
      ]},
    ],
    computeFromSqft: true,
  },
  "junk-removal": {
    label: "Junk Removal", icon: Trash,
    base: { min: 150, max: 550 },
    modifiers: [
      { key: "load", label: "Load size", type: "select", options: [
        { v: "single",  label: "Single item / small pickup", min: -100, max: -350 },
        { v: "quarter", label: "1/4 truck load", min: -50, max: -250 },
        { v: "half",    label: "1/2 truck load", min: 0, max: 0 },
        { v: "full",    label: "Full truck load", min: 100, max: 250 },
      ]},
      { key: "heavy", label: "Heavy / bulky items (appliances, furniture)?", type: "toggle", min: 50, max: 150 },
    ],
  },
  "courier": {
    label: "Courier & Delivery", icon: Truck,
    base: { min: 45, max: 150 },
    modifiers: [
      { key: "type", label: "Delivery type", type: "select", options: [
        { v: "standard", label: "Standard same-day (local)", min: 0, max: 0 },
        { v: "medical",  label: "Medical / priority", min: 45, max: 100 },
        { v: "freight",  label: "General freight / equipment", min: 30, max: 80 },
      ]},
      { key: "long-haul", label: "New Orleans ↔ Baton Rouge run?", type: "toggle", min: 40, max: 90 },
    ],
  },
};

const clampRange = (min, max) => (min > max ? [max, min] : [min, max]);

export default function EstimatePage() {
  const [serviceKey, setServiceKey] = useState("appliance-repair");
  const [values, setValues] = useState({});

  const config = CONFIG[serviceKey];

  const range = useMemo(() => {
    if (!config) return [0, 0];
    let min, max;

    if (config.computeFromSqft) {
      const sqft = parseFloat(values["sqft"]) || 0;
      const rate = config.modifiers.find((m) => m.key === "rate");
      const selected = rate?.options.find((o) => o.v === values["rate"]);
      if (!sqft || !selected) return [0, 0];
      const [lo, hi] = selected.ratePerSqft;
      return [Math.round(sqft * lo), Math.round(sqft * hi)];
    }

    min = config.base.min; max = config.base.max;
    for (const m of config.modifiers) {
      const v = values[m.key];
      if (m.type === "select" && v) {
        const opt = m.options.find((o) => o.v === v);
        if (opt) { min += opt.min || 0; max += opt.max || 0; }
      }
      if (m.type === "toggle" && v === true) { min += m.min || 0; max += m.max || 0; }
    }
    return clampRange(Math.max(0, min), Math.max(0, max));
  }, [serviceKey, values, config]);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="estimate-page">
      <section className="ji-container pt-16 md:pt-24 pb-8">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Calculator size={12} weight="fill" /> Instant Estimate · Range Only
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Get a real estimate —<br /><span className="text-[var(--ji-accent)] italic">in about 60 seconds.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          Pick a service, answer a couple of quick questions, and see a ballpark price range.
        </p>
        <div className="mt-4 max-w-2xl inline-flex items-start gap-2 text-sm text-[var(--ji-muted)] p-3 bg-[var(--ji-surface-alt)] border border-[var(--ji-border)]" style={{ borderRadius: 4 }} data-testid="estimate-disclaimer">
          <Info size={14} weight="duotone" className="text-[var(--ji-accent)] mt-0.5 flex-shrink-0" />
          <span><strong className="text-[var(--ji-text)]">Estimates only give a range of prices — they are not final.</strong> The real quote comes after we see the job on-site. Free.</span>
        </div>
      </section>

      <section className="ji-container pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="ji-card p-7 md:p-10 space-y-6" style={{ borderRadius: 4 }}>
            <div>
              <Label className="text-sm font-medium">Which service?</Label>
              <Select value={serviceKey} onValueChange={(v) => { setServiceKey(v); setValues({}); }}>
                <SelectTrigger data-testid="estimate-service" className="h-12 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CONFIG).map(([k, c]) => <SelectItem key={k} value={k} data-testid={`estimate-service-option-${k}`}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {config.modifiers.map((m) => {
              if (m.type === "select") {
                return (
                  <div key={m.key}>
                    <Label className="text-sm font-medium">{m.label}</Label>
                    <Select value={values[m.key] || ""} onValueChange={(v) => setVal(m.key, v)}>
                      <SelectTrigger data-testid={`estimate-${m.key}`} className="h-12 mt-2"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>
                        {m.options.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              if (m.type === "toggle") {
                const on = values[m.key] === true;
                return (
                  <label key={m.key} className={`ji-card flex items-start gap-3 p-4 cursor-pointer ${on ? "border-[var(--ji-accent)]" : ""}`} style={{ borderRadius: 4 }}>
                    <input type="checkbox" data-testid={`estimate-${m.key}`} checked={on} onChange={(e) => setVal(m.key, e.target.checked)} className="mt-1 h-5 w-5 accent-[var(--ji-accent)]" />
                    <span className="text-sm">{m.label}</span>
                  </label>
                );
              }
              if (m.type === "number") {
                return (
                  <div key={m.key}>
                    <Label className="text-sm font-medium">{m.label}</Label>
                    <Input type="number" data-testid={`estimate-${m.key}`} value={values[m.key] || ""} onChange={(e) => setVal(m.key, e.target.value)} className="h-12 mt-2" placeholder={m.placeholder || ""} />
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>

        <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="ji-card p-8" style={{ borderRadius: 4 }} data-testid="estimate-result">
            <div className="ji-overline mb-4 inline-flex items-center gap-2"><Tag size={12} weight="fill" /> Estimated range</div>
            <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold tracking-tight">{config.label}</div>
            <div className="mt-4 flex items-baseline gap-2">
              <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-5xl md:text-6xl font-bold text-[var(--ji-primary)]">${range[0].toLocaleString()}</span>
              <span className="text-[var(--ji-muted)] text-2xl">—</span>
              <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-5xl md:text-6xl font-bold text-[var(--ji-accent)]">${range[1].toLocaleString()}</span>
            </div>
            <p className="mt-4 text-xs text-[var(--ji-muted)] leading-relaxed">
              This range is an estimate only — the final quote is confirmed on-site (or after we review your job details). All quotes are free.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Link to="/contact" data-testid="estimate-cta-quote" className="ji-btn-accent inline-flex items-center justify-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                Get a real free quote <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/services" data-testid="estimate-cta-services" className="ji-btn-outline inline-flex items-center justify-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                See what's included
              </Link>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
